/**
 * DynamicPage — fetches CMS page and renders with @busmgr/blocks BlockRenderer.
 * Injects siteData, fetchProducts, fetchEvents, and onSubmit so blocks stay decoupled from
 * the API and context layers.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import siteContentApi from '../services/siteContent';
import { BlockRenderer } from '@busmgr/blocks';
import { api } from '../services/api';
import { getApiUrl } from '../services/apiConfig';
import { getTenantFromSubdomain } from '../services/tenant';
import './DynamicPage.css';

const DynamicPage = ({ slug: propSlug }) => {
  const { slug: paramSlug } = useParams();
  const [searchParams] = useSearchParams();
  const site = useSite();
  const slug = propSlug !== undefined ? propSlug : (paramSlug || '');
  const previewToken = searchParams.get('preview') || null;

  const [page, setPage]     = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const pageData = await siteContentApi.getPageWithBlocks(slug, previewToken);
        if (pageData) {
          setPage(pageData); setBlocks(pageData.blocks || []);
          const t = pageData.seo_title || pageData.title;
          document.title = t ? `${t} | ${site.siteName}` : site.defaultSeoTitle || site.siteName;
          const m = document.querySelector('meta[name="description"]');
          if (m && pageData.seo_description) m.setAttribute('content', pageData.seo_description);
        } else {
          const dp = await siteContentApi.getPageBySlug(slug);
          if (dp) {
            setPage(dp);
            setBlocks((dp.sections || []).map(s => ({
              id: s.id, block_type: s.section_type,
              content: s.settings || {}, settings: {}, display_order: s.sort_order
            })));
          } else { setError('Page not found'); }
        }
      } catch (err) { console.error(err); setError('Failed to load page'); }
      finally { setLoading(false); }
    };
    load();
  }, [slug, previewToken, site.siteName, site.defaultSeoTitle]);

  // Injected props for blocks that need them
  const siteData = {
    contactInfo:   site.contactInfo,
    businessHours: site.businessHours,
    siteName:      site.siteName,
  };

  const fetchProducts = useCallback(async ({ limit, categorySlug }) => {
    let ep = `/items?limit=${limit}&status=active`;
    if (categorySlug) ep += `&category=${categorySlug}`;
    const res = await api.get(ep);
    if (!res.ok) return [];
    const data = await res.json();
    return data.data || [];
  }, []);

  const fetchEvents = useCallback(async ({ source = 'events', limit = 20, range = 'upcoming' } = {}) => {
    const qs = new URLSearchParams();
    qs.set('source', source);
    qs.set('range', range);
    if (limit) qs.set('limit', String(limit));
    const res = await api.get(`/events/upcoming?${qs}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.data || [];
  }, []);

  const onSubmit = useCallback(async (formData) => {
    const res = await api.post('/contact/submit', formData);
    if (res.ok) return { ok: true };
    const d = await res.json().catch(() => ({}));
    return { ok: false, error: d.error || 'Failed to send.' };
  }, []);

  if (loading) return (
    <div className="dynamic-page dynamic-page--loading">
      <div className="loading-spinner"><div className="spinner" /><p>Loading…</p></div>
    </div>
  );
  if (error) return (
    <div className="dynamic-page dynamic-page--error">
      <div className="error-message"><h1>Page Not Found</h1><p>The page you're looking for doesn't exist or isn't published yet.</p></div>
    </div>
  );
  if (!page || !blocks.length) return (
    <div className="dynamic-page dynamic-page--empty">
      <div className="empty-message"><h1>{page?.title || 'Untitled Page'}</h1><p>This page has no content yet.</p></div>
    </div>
  );

  return (
    <div className="dynamic-page">
      <BlockRenderer
        blocks={blocks}
        injectedProps={{
          siteData,
          fetchProducts,
          fetchEvents,
          onSubmit,
          tenantSlug: getTenantFromSubdomain(),
          apiBase: getApiUrl(),
        }}
      />
    </div>
  );
};

export default DynamicPage;
